import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import apiService from '../services/api';
import { User } from '../types/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Initialize authentication state
   * Validates existing token and loads user profile
   */
  const initAuth = useCallback(async () => {
    // Check if we have a valid token
    if (!apiService.isAuthenticated()) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      // Validate token with server and get user profile
      const response = await apiService.getProfile();
      if (response.data) {
        setUser(response.data);
      } else {
        // No user data returned, clear auth
        apiService.clearAllAuthData();
        setUser(null);
      }
    } catch (error: any) {
      // Detect "network error" (CORS rejection, server down, DNS, etc.) where
      // the request never produced a response. The user is then stuck because
      // any stale token can't be validated. Auto-recover by clearing all
      // browser auth state and reloading once. The sessionStorage flag prevents
      // an infinite reload loop if the API stays unreachable.
      const isNetworkError = !error?.response && !!error?.message;
      const alreadyRecovered = sessionStorage.getItem('auth_auto_recovered') === '1';

      console.warn('Auth validation failed:', error?.message);
      apiService.clearAllAuthData();
      setUser(null);

      if (isNetworkError && !alreadyRecovered) {
        sessionStorage.setItem('auth_auto_recovered', '1');
        // Best-effort: also drop any other app state so we land on a clean login
        try { localStorage.clear(); } catch {}
        window.location.reload();
        return;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Successful auth means the API is reachable — clear the recovery flag so
  // a future genuine network error can trigger another auto-recovery.
  useEffect(() => {
    if (user) sessionStorage.removeItem('auth_auto_recovered');
  }, [user]);

  // Initialize on mount
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  /**
   * Login with email and password
   * Clears any existing auth data before attempting login
   */
  const login = async (email: string, password: string) => {
    try {
      const response = await apiService.login({ email, password });

      if (response.user) {
        setUser(response.user);
      } else {
        // If no user in response, fetch profile
        const profileResponse = await apiService.getProfile();
        if (profileResponse.data) {
          setUser(profileResponse.data);
        }
      }
    } catch (error: any) {
      // Ensure clean state on login failure
      apiService.clearAllAuthData();
      setUser(null);
      throw error;
    }
  };

  /**
   * Logout - clears all auth data
   */
  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed, clearing local data');
    } finally {
      // Always clear user state
      setUser(null);
    }
  };

  /**
   * Refresh authentication state
   * Useful for re-validating after network issues
   */
  const refreshAuth = async () => {
    setLoading(true);
    await initAuth();
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
